import React, { useEffect, useMemo, useRef, useState } from "react";

function clamp01(n) {
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function makeStableId(prefix) {
  return prefix + "-" + Math.random().toString(36).slice(2);
}

/**
 * Convert waveform JSON (your format) into a symmetric filled waveform path.
 * Uses json.data (numbers), normalizes by max abs value.
 */
function waveformJsonToFilledPath(json, width, height) {
  if (!json || !Array.isArray(json.data)) {
    throw new Error("Invalid waveform JSON: expected { data: number[] }");
  }

  var arr = json.data;
  if (arr.length < 2) throw new Error("Waveform data too short");

  // Find max abs for normalization
  var maxAbs = 0;
  for (var i = 0; i < arr.length; i++) {
    var a = Math.abs(Number(arr[i]));
    if (a > maxAbs) maxAbs = a;
  }
  if (maxAbs === 0) maxAbs = 1;

  var mid = height / 2;
  var step = width / (arr.length - 1);

  // Top edge
  var d = "M 0 " + mid;
  for (var x = 0; x < arr.length; x++) {
    var p = Math.abs(Number(arr[x])) / maxAbs; // 0..1
    var X = x * step;
    var Y = mid - p * mid;
    d += " L " + X + " " + Y;
  }

  // Bottom edge (reverse)
  for (var r = arr.length - 1; r >= 0; r--) {
    var p2 = Math.abs(Number(arr[r])) / maxAbs;
    var X2 = r * step;
    var Y2 = mid + p2 * mid;
    d += " L " + X2 + " " + Y2;
  }

  d += " Z";
  return d;
}

function waveformJsonToLinePath(json, width, height) {
  if (!json || !Array.isArray(json.data)) {
    throw new Error("Invalid waveform JSON: expected { data: number[] }");
  }

  var arr = json.data;
  if (arr.length < 2) throw new Error("Waveform data too short");

  // Normalize by max absolute value
  var maxAbs = 0;
  for (var i = 0; i < arr.length; i++) {
    var a = Math.abs(Number(arr[i]));
    if (a > maxAbs) maxAbs = a;
  }
  if (maxAbs === 0) maxAbs = 1;

  var mid = height / 2;
  var step = width / (arr.length - 1);

  var d = "M 0 " + mid;

  for (var x = 0; x < arr.length; x++) {
    var v = Number(arr[x]) / maxAbs; // -1 .. +1
    var X = x * step;
    var Y = mid - v * mid;           // up AND down
    d += " L " + X + " " + Y;
  }

  return d;
}

export default function WaveformWithHighlights({
  waveformDataUrl,
  width = 800,
  height = 100,
  ranges = [], // [{from:0..1,to:0..1}]
  // baseFill = "#90ee90",
  baseFill = "rgb(var(--primary))",
  // highlightFill = "rgba(113, 2, 92, 0.25)",
  highlightFill = "rgb(var(--secondary))",
  stroke = "rgb(var(--primary))",
  // stroke = "rgb(var(--bg))",
  strokeWidth = 1,
  ID = "",
  duration = ""
}) {

  const [json, setJson] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // const clipIdRef = useRef(makeStableId("wf-clip"));
  const clipIdRef = useRef(`wf-clip-${ID}`);
  const clipId = clipIdRef.current;

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);
    setJson(null);

    fetch(waveformDataUrl)
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .then(function (data) {
        if (cancelled) return;
        setJson(data);
        setLoading(false);
      })
      .catch(function (e) {
        if (cancelled) return;
        setError(e.message);
        setLoading(false);
      });

    return function () {
      cancelled = true;
    };
  }, [waveformDataUrl]);

  const pathD = useMemo(() => {
    if (!json) return null;
    return waveformJsonToLinePath(json, width, height);
  }, [json, width, height]);

  const rects = useMemo(() => {
    return (ranges || [])
      .map(function (r) {
        // const from = clamp01(r.from);
        // const to = clamp01(r.to);
        // const x = from * width;
        // const w = Math.max(0, (to - from) * width);
        // return { x: x, w: w };

        return {x : r.offset * (width/duration), w : r.duration * (width/duration)}
      })
      .filter(function (rw) {
        return rw.w > 0;
      });
  }, [ranges, width]);

  if (loading) return <div>Loading…</div>;
  if (error) return <div>Waveform error: {error}</div>;
  if (!pathD) return <div>No waveform path</div>;

  return (
    <svg className="w-full" width={width} height={height} viewBox={"0 0 " + width + " " + height}>
      {/* Base waveform shape */}
      <path d={pathD} fill={baseFill} stroke={stroke} strokeWidth={strokeWidth} />

      <defs>
        <clipPath id={clipId}>
          {rects.map(function (r, i) {
            return <rect key={i} x={r.x} y={0} width={r.w} height={height} />;
          })}
        </clipPath>
      </defs>

      {/* Highlight overlay (same shape clipped to ranges) */}
      <path d={pathD} fill={highlightFill} clipPath={"url(#" + clipId + ")"} />
    </svg>
  );
}
